import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Field, Label } from "@headlessui/react";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";

import { formatMinutes } from "../../../../app/datetimeUtils";
import { useAppSelector } from "../../../../app/hooks";
import { getParamAsInt } from "../../../../app/utils";
import { SelectList } from "../../../../components/input/SelectList";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import FormattedDateAndTime from "../../../../components/FormattedDateAndTime";
import { Loading } from "../../../../components/Loading";
import { DownloadButton } from "../../shared/DownloadButton";
import { LessonUsageByCategoryChart } from "./LessonUsageByCategoryChart";
import { TutorialUsage } from "./TutorialUsageByCourseAndFocusViewModel";
import { useTutorialUsageByCourseAndFocusQuery } from "./courseUsageReportApiSlice";

export default function LessonUsageByCategoryReport() {
	const [queryParams] = useSearchParams();
	const initialCategoryId = getParamAsInt(queryParams, "categoryId") ?? 0;
	const courseId = getParamAsInt(queryParams, "courseId");
	const { aspUrl } = useAppSelector((state) => state.site);

	const [categoryId, setCategoryId] = useState(initialCategoryId);
	const result = useTutorialUsageByCourseAndFocusQuery({
		courseId: courseId,
		focusId: categoryId,
	});
	const data = result.data;

	const handleCategoryChange = (categoryId: string) => {
		setCategoryId(Number(categoryId));
	};

	return (
		<main>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Lesson Usage by Category</h1>
				</div>
			</div>

			<p className="pt-8">
				The report below shows the most recent date of use and the total time spent using
				the lessons within the chosen category.
			</p>
			<Loading loading={result.isLoading} />
			{data && (
				<>
					<div className="flex justify-between items-end mt-4">
						<Field className="w-1/2 mt-4">
							<Label
								htmlFor="category-list"
								className="block font-semibold text-sm text-gray-900">
								Category
							</Label>
							<SelectList
								id="category-list"
								className="w-full mt-1"
								selectListItems={data.allFoci}
								value={categoryId.toString()}
								onChange={handleCategoryChange}
							/>
						</Field>
						<div>
							<DownloadButton
								url={`${aspUrl}/Report/DownloadTutorialUsageFilteredByCourseAndFocus?courseId=${data.courseId}&categoryId=${categoryId}`}
								disabled={data.tutorials.length == 0}
							/>
						</div>
					</div>

					<LessonUsageByCategoryTable
						className="w-full mt-4"
						courseId={courseId!}
						tutorialUsageByCategory={data.tutorials}
						tutorialUsageForCourse={data.tutorialUsageForCourse}
					/>

					<LessonUsageByCategoryChart
						className="mt-4"
						courseId={data.courseId}
						data={data.tutorials}
					/>
				</>
			)}
		</main>
	);
}

function LessonUsageByCategoryTable(props: {
	className?: string;
	courseId: number;
	tutorialUsageByCategory: TutorialUsage[];
	tutorialUsageForCourse: TutorialUsage;
}) {
	const hasData = props.tutorialUsageByCategory && props.tutorialUsageByCategory.length > 0;

	const lessonBodyTemplate = (rowData: TutorialUsage) => {
		return (
			<Link
				className="link"
				to={`/reports/course/usage/lesson?lessonId=${rowData.lessonId}&categoryId=${rowData.tutorialCategoryId}&courseId=${props.courseId}`}>
				{rowData.lessonName}
			</Link>
		);
	};

	const lastUsedBodyTemplate = (rowData: TutorialUsage) => {
		return <FormattedDateAndTime date={rowData.lastUsedDate} />;
	};

	const timeSpentBodyTemplate = (rowData: TutorialUsage) => {
		return formatMinutes(rowData.timeSpentMinutes);
	};

	const footerGroup = (
		<ColumnGroup>
			<Row>
				<Column footer="Total Time" colSpan={2} footerClassName="font-medium !text-right" />
				<Column
					footer={formatMinutes(props.tutorialUsageForCourse.timeSpentMinutes)}
					footerClassName="font-medium"
				/>
			</Row>
		</ColumnGroup>
	);

	return (
		<DataTable
			value={props.tutorialUsageByCategory}
			dataKey="lessonId"
			loading={props.tutorialUsageByCategory == undefined}
			emptyMessage="No results found"
			stripedRows
			sortIcon={CustomSortIcon}
			className={`block tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className}`}
			footerColumnGroup={hasData && footerGroup}>
			<Column
				header="Lesson"
				body={lessonBodyTemplate}
				sortable
				sortField="lessonName"></Column>
			<Column
				header="Most Recent Use"
				body={lastUsedBodyTemplate}
				sortable
				sortField="lastUsedDate"></Column>
			<Column
				header="Total Time Spent"
				body={timeSpentBodyTemplate}
				sortable
				sortField="timeSpentMinutes"></Column>
		</DataTable>
	);
}
